import { addLocalDays } from "@normax-performance/shared-format";

export interface ReportPeriodRange {
  from: string; // "YYYY-MM-DD"
  to: string; // "YYYY-MM-DD"
}

/** Superset of Analytics' `QuickPeriodKey` (task spec §6) — `custom` and
 * `lastRecordedDay` are NOT resolved by `computeReportPeriod` (custom is
 * free user input; lastRecordedDay requires an async
 * `analyticsApi.latestReportingDay()` call, resolved once and then frozen
 * into an explicit `{from,to}` by `ReportPeriodControl`, Task 15 — task
 * spec §7: "resolves one date and then freezes that date," never kept live). */
export type ReportPeriodPresetKey = "yesterday" | "thisWeek" | "previousWeek" | "thisMonth" | "previousMonth" | "thisYear" | "custom" | "lastRecordedDay";

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function previousCompleteMonth(date: Date): ReportPeriodRange {
  const firstOfPrevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const lastOfPrevMonth = new Date(date.getFullYear(), date.getMonth(), 0);
  return { from: isoDate(firstOfPrevMonth), to: isoDate(lastOfPrevMonth) };
}

function startOfWeek(date: Date): Date {
  const daysSinceMonday = (date.getDay() + 6) % 7;
  return addLocalDays(date, -daysSinceMonday);
}

export type ResolvableReportPeriodPresetKey = Exclude<ReportPeriodPresetKey, "custom" | "lastRecordedDay">;

/**
 * Local-calendar-day arithmetic only (never UTC/ms), same discipline as
 * `apps/web/src/features/analytics/period.ts`. `custom`/`lastRecordedDay`
 * are excluded from this function's domain by type — see the type comment.
 */
export function computeReportPeriod(key: ResolvableReportPeriodPresetKey, today: Date): ReportPeriodRange {
  switch (key) {
    case "yesterday": {
      const yesterday = addLocalDays(today, -1);
      return { from: isoDate(yesterday), to: isoDate(yesterday) };
    }
    case "thisWeek": {
      const monday = startOfWeek(today);
      const yesterday = addLocalDays(today, -1);
      if (monday > yesterday) {
        const previousMonday = addLocalDays(monday, -7);
        const previousSunday = addLocalDays(monday, -1);
        return { from: isoDate(previousMonday), to: isoDate(previousSunday) };
      }
      return { from: isoDate(monday), to: isoDate(yesterday) };
    }
    case "previousWeek": {
      // Mirrors `thisWeek`'s own Monday fallback: on Monday, "this week"
      // falls back to the previous complete week, so "previous week" must
      // shift back an extra week too — always the full ISO week immediately
      // before whichever week `thisWeek` itself resolves to.
      const monday = startOfWeek(today);
      const yesterday = addLocalDays(today, -1);
      const effectiveMonday = monday > yesterday ? addLocalDays(monday, -7) : monday;
      const previousMonday = addLocalDays(effectiveMonday, -7);
      const previousSunday = addLocalDays(effectiveMonday, -1);
      return { from: isoDate(previousMonday), to: isoDate(previousSunday) };
    }
    case "thisMonth": {
      if (today.getDate() === 1) return previousCompleteMonth(today);
      const yesterday = addLocalDays(today, -1);
      return { from: isoDate(startOfMonth(today)), to: isoDate(yesterday) };
    }
    case "previousMonth":
      return previousCompleteMonth(today);
    case "thisYear": {
      const yesterday = addLocalDays(today, -1);
      const jan1 = new Date(today.getFullYear(), 0, 1);
      if (jan1 > yesterday) {
        const prevJan1 = new Date(today.getFullYear() - 1, 0, 1);
        const prevDec31 = new Date(today.getFullYear() - 1, 11, 31);
        return { from: isoDate(prevJan1), to: isoDate(prevDec31) };
      }
      return { from: isoDate(jan1), to: isoDate(yesterday) };
    }
  }
}
